import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';

/**
 * Desde un origen público (p. ej. ngrok al front) el navegador bloquea XMLHttpRequest a loopback
 * (Private Network Access). Si abrís el SPA por túnel, definí VITE_API_BASE_URL_PUBLIC con la URL
 * pública del backend (otro ngrok al puerto 3001), p. ej. https://xxxx.ngrok-free.app/api
 */
function resolveApiBaseUrl(): string {
  const local = (
    import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api'
  ).replace(/\/$/, '');
  const publicBase = import.meta.env.VITE_API_BASE_URL_PUBLIC?.trim().replace(/\/$/, '');

  if (typeof window === 'undefined') {
    return local;
  }

  const h = window.location.hostname;
  const viaLoopback =
    h === 'localhost' || h === '127.0.0.1' || h.endsWith('.localhost');

  if (!viaLoopback && publicBase) {
    return publicBase;
  }

  if (!viaLoopback && !publicBase && import.meta.env.DEV) {
    console.warn(
      '[httpClient] Origen no local sin VITE_API_BASE_URL_PUBLIC: las llamadas a localhost:3001 pueden ser bloqueadas por el navegador. Configurá VITE_API_BASE_URL_PUBLIC con la URL pública del API.'
    );
  }

  return local;
}

const baseURL = resolveApiBaseUrl();
const timeout = Number(import.meta.env.VITE_API_TIMEOUT) || 10000;

export const httpClient: AxiosInstance = axios.create({
  baseURL,
  timeout,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request Interceptor
httpClient.interceptors.request.use(
  (config: AxiosRequestConfig) => {
    // Intentar obtener el token de diferentes lugares por compatibilidad
    // Prioridad: admin_token (usado por el frontend) > authToken > token
    const token = localStorage.getItem('admin_token') || 
                  localStorage.getItem('authToken') ||
                  localStorage.getItem('token');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response Interceptor
httpClient.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  (error) => {
    const url = error.config?.url || 'unknown';
    const status = error.response?.status;
    
    if (status === 401) {
      // NO limpiar ni redirigir si es el endpoint /auth/me o /auth/login
      const isLoginEndpoint = url.includes('/auth/login');
      const isMeEndpoint = url.includes('/auth/me');
      const isCurrentPathLogin = window.location.pathname === '/login';
      
      // Ignorar errores de /auth/me - es opcional y no debe causar logout
      if (isMeEndpoint) {
        return Promise.reject(error);
      }
      
      // No hacer nada si es el endpoint de login
      if (isLoginEndpoint || isCurrentPathLogin) {
        return Promise.reject(error);
      }
      
      // Solo limpiar y redirigir para otros endpoints con 401
      localStorage.removeItem('authToken');
      localStorage.removeItem('admin_token');
      localStorage.removeItem('token');
      localStorage.removeItem('admin_user');
      localStorage.removeItem('user');
      
      // Redirigir a login solo si no estamos ya ahí
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export default httpClient;
